import { type APIDocumentResponse, type APIListResponse } from "~/utils/types";
import { useUser } from "@/stores/user";

export async function query<T>(
	path: string,
	id?: string,
	query: any = {},
	fetchOptions: { signal?: AbortSignal } = {}
): Promise<{ list: T[]; length: number; id: string; exported: number; exported_count_token?: string; num_contacts: number; contact_count_company_count: number }> {
	const token = useUser().token;

	Object.keys(query).forEach((key) => (query[key] === undefined ? delete query[key] : {}));

	const url = getHostUrl() + `/${path}` + buildQueryString(query);

	if (!id) {
		id = url;
	}

	const options: RequestInit = {
		method: "GET",
		headers: {
			"Content-Type": "application/json",
			Authorization: token ? `Bearer ${token}` : "",
		},
		signal: fetchOptions.signal,
	};

	//console.log("store.query", url, options);

	const data = await fetch(url, options);

	if (data.status !== 200) throw new Error(`Error fetching ${path}`);

	const json = (await data.json()) as APIListResponse<T>;

	if (json.success !== true) throw new Error(`Error fetching ${path}`);

	return {
		list: json.list,
		length: json.length,
		id,
		exported: typeof json.exported === "number" ? json.exported : 0,
		exported_count_token: json.exported_count_token,
		num_contacts: json.num_contacts ? json.num_contacts : 0,
		contact_count_company_count: json.contact_count_company_count ? json.contact_count_company_count : 0,
	};
}

export async function record<T>(path: string, query: any = {}): Promise<T> {
	const token = useUser().token;

	const url = getHostUrl() + `/${path}` + buildQueryString(query);

	const options: any = {
		method: "GET",
		headers: {
			"Content-Type": "application/json",
			Authorization: token ? `Bearer ${token}` : "",
		},
	};

	//console.log("store.record", url, options);

	const data = await fetch(url, options);

	if (data.status !== 200) throw new Error(`Error fetching ${path}`);

	const json = (await data.json()) as APIDocumentResponse<T>;

	if (json.success !== true) throw new Error(`Error fetching ${path}`);

	return json.data;
}

export async function input<T>(path: string, body: any, query: any = {}, method: string = "PUT", jsonType: boolean = true): Promise<T> {
	const token = useUser().token;

	const url = getHostUrl() + `/${path}` + buildQueryString(query);

	const headers: any = {
		Authorization: token ? `Bearer ${token}` : "",
	};

	if (jsonType) {
		headers["Content-Type"] = "application/json";
	}

	const options: any = {
		method,
		headers,
		body: jsonType ? JSON.stringify(body) : body,
	};

	//console.log("store.input", url, options);

	const response = await fetch(url, options);

	if (!response.ok) {
		let errorBody: any = {};
		try {
			errorBody = await response.json();
		} catch (error) {
			errorBody = {};
		}

		throw {
			status: response.status,
			message: errorBody?.detail || errorBody?.message || response.statusText,
			...errorBody,
		};
	}

	const json = (await response.json()) as APIDocumentResponse<T> | boolean;

	if (typeof json === "boolean") {
		return json as T;
	}

	if (json.success !== true) {
		throw { status: response.status, ...json };
	}
	return json.data;
}

export function getHostUrl() {
	const config = useRuntimeConfig();

	if (process.server) {
		const configuredHost = String(config.host_url || "");
		const requestOrigin = useRequestURL().origin;
		const host = import.meta.dev && requestOrigin ? requestOrigin : configuredHost;
		return host + "/api";
	} else {
		return window.location.origin + "/api";
	}
}

function buildQueryString(query: any = {}) {
	if (Object.keys(query).length === 0) return "";

	// Remove empty values
	Object.keys(query).forEach((key) => (query[key] === undefined || query[key] === "{}" ? delete query[key] : {}));

	const params = new URLSearchParams();
	Object.entries(query).forEach(([key, value]) => {
		if (Array.isArray(value)) {
			value.forEach((item) => params.append(key, String(item)));
			return;
		}
		params.set(key, String(value));
	});

	const queryString = params.toString();
	return queryString ? `?${queryString}` : "";
}
